import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { BorderBox, SectionHead } from '../components/Primitives';
import { callFunction, callableErrorMessage } from '../lib/firebase';
import { PRIZE_EMAIL_KEY } from '../lib/session';
import { join as copy } from '../content/copy';
import type { LeadInterest, LeadSubmission } from '../types';
import './Join.css';

/**
 * Interest form (spec §6.2).
 *
 * Lead capture, not registration. Five fields, one screen.
 *
 * Note what is NOT here: no WhatsApp link, no join code, no league ID. Those are
 * gated behind payment on /welcome/{slug}. The success screen deliberately gives
 * away nothing, so this endpoint is worthless to scrape.
 */

interface FormState {
  name: string;
  email: string;
  phone: string;
  referredBy: string;
  interest: LeadInterest;
  message: string;
  website: string; // honeypot
}

const EMPTY: FormState = {
  name: '',
  email: '',
  phone: '',
  referredBy: '',
  interest: 'main',
  message: '',
  website: '',
};

export default function Join() {
  const [form, setForm] = useState<FormState>(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  /*
   * Whether the league is still taking entries, from the server.
   *
   * Null while the answer is in flight, and it stays null if the call fails.
   * Both cases render the open copy, which is the right way round to be wrong:
   * the form works identically either way, and the SUCCESS screen is driven by
   * what the server says it actually did with the submission — not by this. So
   * the worst a failed fetch costs is a heading that oversells the deadline for
   * as long as it takes to press the button.
   *
   * The hard rule is enforced in submitLead, which reads the same config and
   * files a late entry to the waiting list whatever this page believed.
   */
  const [entriesOpen, setEntriesOpen] = useState<boolean | null>(null);
  const [waitlisted, setWaitlisted] = useState(false);

  useEffect(() => {
    let cancelled = false;
    callFunction<Record<string, never>, { entriesOpen: boolean }>('getSiteState', {})
      .then((res) => {
        if (!cancelled) setEntriesOpen(res.entriesOpen);
      })
      .catch(() => {
        /* Leave it null and show the open copy. See the note above. */
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Null (unknown) reads as open. The closed copy only ever appears on a
  // definite `false` from the server.
  const closed = entriesOpen === false;
  const text = closed ? { ...copy, ...copy.closed } : copy;

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    if (submitting) return;

    setError(null);
    setSubmitting(true);

    try {
      const result = await callFunction<LeadSubmission, { ok: boolean; waitlist?: boolean }>('submitLead', {
        name: form.name,
        email: form.email,
        phone: form.phone || undefined,
        referredBy: form.referredBy,
        interest: form.interest,
        message: form.message || undefined,
        website: form.website,
      });
      /*
       * Someone who has just handed over their name, email and referrer has
       * more than cleared the bar /prizes sets for showing figures. Seeding the
       * key here means the payout page is already unlocked when they follow the
       * link below, rather than asking for the same address a second time.
       */
      localStorage.setItem(PRIZE_EMAIL_KEY, form.email);
      // The server's word, not this page's guess. If the deadline passed while
      // the form was open in a tab, this is what catches it.
      setWaitlisted(result.waitlist === true);
      setDone(true);
    } catch (err) {
      setError(
        callableErrorMessage(
          err,
          "That didn't go through. Try again, or email theref@refsabanker.com.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <Layout>
        <div className="stripes">
          <section className="wrap section join__done">
            <p className="eyebrow">Received</p>
            <h1>{waitlisted ? copy.closed.successHeading : copy.successHeading}</h1>
            <p className="lede join__done-body">
              {waitlisted ? copy.closed.successBody : copy.successBody}
            </p>
            <div className="join__done-actions">
              <Link to="/prizes" className="btn">
                See what is on the table
              </Link>
              <Link to="/history" className="btn btn--ghost">
                Read up on who you&rsquo;re against
              </Link>
            </div>
          </section>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="stripes">
        <section className="wrap wrap--wide join__head">
          <div>
            <p className="eyebrow">{text.eyebrow}</p>
            <h1>{text.heading}</h1>
          </div>
          <p className="lede join__lede">{text.lede}</p>
        </section>

        <section className="wrap wrap--wide join__body">
          <div className="join__well">
            <p className="eyebrow">{text.formLabel}</p>

            <form className="join-form" onSubmit={handleSubmit} noValidate>
              {error && (
                <p className="form-error" role="alert">
                  {error}
                </p>
              )}

              <label className="field">
                <span className="field__label">{copy.form.name.label}</span>
                <input
                  className="field__input"
                  type="text"
                  required
                  autoComplete="name"
                  placeholder={copy.form.name.placeholder}
                  value={form.name}
                  onChange={(e) => update('name', e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">{copy.form.email.label}</span>
                <input
                  className="field__input"
                  type="email"
                  required
                  autoComplete="email"
                  inputMode="email"
                  placeholder={copy.form.email.placeholder}
                  value={form.email}
                  onChange={(e) => update('email', e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">
                  {copy.form.phone.label}{' '}
                  <span className="field__hint field__hint--inline">
                    {copy.form.phone.optional}
                  </span>
                </span>
                <input
                  className="field__input"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder={copy.form.phone.placeholder}
                  value={form.phone}
                  onChange={(e) => update('phone', e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">{copy.form.referredBy.label}</span>
                <input
                  className="field__input"
                  type="text"
                  required
                  placeholder={copy.form.referredBy.placeholder}
                  value={form.referredBy}
                  onChange={(e) => update('referredBy', e.target.value)}
                />
              </label>

              <label className="field">
                <span className="field__label">{copy.form.leagues.label}</span>
                <select
                  className="field__select"
                  value={form.interest}
                  onChange={(e) => update('interest', e.target.value as LeadInterest)}
                >
                  <option value="main">Main league</option>
                  <option value="main_and_high_rollers">
                    Main league + High Rollers
                  </option>
                </select>
              </label>

              <label className="field">
                <span className="field__label">
                  {copy.form.message.label}{' '}
                  <span className="field__hint field__hint--inline">
                    {copy.form.message.optional}
                  </span>
                </span>
                <textarea
                  className="field__textarea"
                  rows={4}
                  placeholder={copy.form.message.placeholder}
                  value={form.message}
                  onChange={(e) => update('message', e.target.value)}
                />
              </label>

              {/*
                Honeypot. Hidden from sighted users and from screen readers, and
                excluded from tab order — a human will never fill it, so anything
                that does is a bot. Server-side check lives in submitLead.
              */}
              <div className="honeypot" aria-hidden="true">
                <label>
                  Website
                  <input
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={(e) => update('website', e.target.value)}
                  />
                </label>
              </div>

              <button className="btn btn--block join__submit" type="submit" disabled={submitting}>
                {submitting ? copy.form.submitting : copy.form.submit}
              </button>

              <p className="join__reassure">{copy.form.reassurance}</p>

              <p className="join__aside">
                Or write to{' '}
                <a className="join__link" href="mailto:theref@refsabanker.com">
                  theref@refsabanker.com
                </a>{' '}
                directly. We use this to get in touch about the league and nothing
                else.
              </p>
            </form>
          </div>

          <div className="join__side">
            <SectionHead title={text.next.heading} count={text.next.count} level={2} />
            <ol className="next-steps">
              {text.next.steps.map((step, i) => (
                <li key={step.title} className="next-steps__item">
                  <span className="next-steps__num" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <strong className="next-steps__title">{step.title}</strong>
                    <span className="next-steps__detail">{step.detail}</span>
                  </div>
                </li>
              ))}
            </ol>

            {/*
              The entry fee is the one current-season figure allowed on a public
              surface (spec §5): a fixed, knowable cost, not a projection that
              moves with the entrant count. The payouts are not here.
            */}
            <BorderBox label={copy.fee.label} tone="gold" note={copy.fee.note}>
              <p className="join__fee">{copy.fee.amount}</p>
            </BorderBox>

            <BorderBox label={text.closes.label} tone="soft" note={text.closes.note}>
              <p className="join__closes">{text.closes.when}</p>
            </BorderBox>

            <p className="note join__existing">{copy.existingPlayerNote}</p>
          </div>
        </section>
      </div>

      <section className="invert section">
        <div className="wrap wrap--wide">
          <h3 className="join__faq-heading">{copy.faq.heading}</h3>
          <div className="join__faq">
            {copy.faq.items.map((item) => (
              <div key={item.q} className="join__faq-item">
                <h4 className="join__faq-q">{item.q}</h4>
                <p className="join__faq-a">{item.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
}
